<template>
  <div
    class="tw:flex-col tw:flex tw:w-full tw:md:max-w-(--tw-breakpoint-lg) tw:lg:max-w-(--tw-breakpoint-xl) tw:mr-auto tw:ml-auto"
  >
    <BaseCard no-padding class="card">
      <div class="tw:p-6 tw:pb-8">
        <div class="tw:text-gray-800">
          <h2 class="tw:text-xl tw:font-bold tw:pb-2.5">{{ $t('rc_bidding_head') }}</h2>
          <p class="tw:w-full tw:md:w-3/5 tw:font-normal tw:text-sm">{{ $t('rc_bidding_head_subtext') }}</p>
        </div>
        <BiddingModeGraph
          :bidding-modes="biddingModes"
          :selectedCPCLevel="selectedCPCLevel"
          @onNewModeSelected="onNewModeSelected"
        />
        <BiddingModeDescription :bidding-modes="biddingModes" :selectedCPCLevel="selectedCPCLevel" />
      </div>
      <div
        class="tw:flex tw:border-t tw:border-gray-300 tw:px-6 tw:py-8 tw:md:flex-row tw:flex-col"
        :class="isSelectedCPCDifferentFromCurrent ? 'tw:justify-between' : 'tw:justify-end'"
      >
        <GhostButton
          v-show="isSelectedCPCDifferentFromCurrent"
          class="tw:px-9 tw:order-2 tw:md:order-1 tw:mb-4 tw:md:mb-0"
          size="medium"
          @click="resetSelection"
          >{{ $t('cancel') }}</GhostButton
        >
        <div
          v-show="isSelectedCPCDifferentFromCurrent"
          class="tw:flex tw:justify-end tw:order-1 tw:md:order-2 tw:mb-4 tw:md:mb-0 tw:mx-2"
        >
          <p class="tw:text-sm tw:text-center tw:md:text-right tw:w-full tw:md:w-[60%] tw:leading-[18px]">
            {{ $t('rc_bidding_change_effect') }}
          </p>
        </div>
        <MainButton
          class="tw:px-9 tw:order-3"
          size="medium"
          :disabled="!isSelectedCPCDifferentFromCurrent || isUpdating"
          @click="submitNewBiddingGoal"
        >
          <span v-if="!isUpdating">{{ $t('save') }}</span>
          <span v-else><SmallDotLoader color="#fff" /></span>
        </MainButton>
      </div>
    </BaseCard>
    <FullScreenLoader v-show="isUpdatingBiddingMode || isUpdating" />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

import BaseCard from '@/components/dashboard/BaseCard.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import BiddingModeGraph from '@/components/rateConnect/biddingGoals/BiddingModeGraph.vue'
import BiddingModeDescription from '@/components/rateConnect/biddingGoals/BiddingModeDescription.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import FullScreenLoader from '@/components/base/loaders/FullScreenLoader.vue'

import { useActiveMarkets, useCampaignMutation } from '@/components/rateConnect/queries'
import { selectedAccommodationId } from '@/layouts/queries'

import useToastNotifications from '@/components/notifications/useToastNotifications'

const { displayNotification, showSaveError } = useToastNotifications()
const { updatingBiddingMode, isUpdatingBiddingMode } = useCampaignMutation()
const { activeMarkets } = useActiveMarkets()

const biddingModes = ['low', 'auto', 'high', 'boost']
const newCPCLevel = ref(null)
const isUpdating = ref(false)

const campaignCurrentCPCLevel = computed(() => activeMarkets.value?.[0]?.cpcLevel || 'auto')
const selectedCPCLevel = computed(() => newCPCLevel.value ?? campaignCurrentCPCLevel.value)
const isSelectedCPCDifferentFromCurrent = computed(() => newCPCLevel.value !== campaignCurrentCPCLevel.value)

const resetSelection = () => {
  newCPCLevel.value = campaignCurrentCPCLevel.value
}
const onNewModeSelected = newVal => (newCPCLevel.value = newVal)

const submitNewBiddingGoal = () => {
  isUpdating.value = true
  const currentCampaigns = activeMarkets.value
  if (!currentCampaigns || !Array.isArray(currentCampaigns) || currentCampaigns.length < 1) return

  updatingBiddingMode(
    {
      accommodationId: selectedAccommodationId.value,
      body: {
        biddingGoal: selectedCPCLevel.value,
      },
    },
    {
      onSuccess: () => {
        isUpdating.value = false
        displayNotification({
          message: 'rc_bidding_change_success',
          isTranslationKey: true,
          type: 'success',
        })
      },
      onError: () => {
        isUpdating.value = false
        showSaveError()
      },
    }
  )
}
</script>
<style scoped>
.card {
  box-shadow:
    0 2px 4px 0 rgba(63, 87, 189, 0.1),
    0 2px 10px 0 rgba(108, 112, 122, 0.15);
  background-color: var(--tw-color-white);
}
</style>
